import { useTranslation } from 'react-i18next';
import { format, isBefore, addDays, isWeekend, isSameDay } from 'date-fns';
import * as yup from 'yup';
import isWithinTimeRange from '@medflex/utils/isWithinTimeRange';
import getFullDate from '@medflex/utils/getFullDate';

const germanDateRegExp = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[012])\.\d{4}$/;
const germanTimeRegExp = /^(([1-9]|[0-1]\d|[2][0-3]):[0-5]\d)$/;
const MAX_DESCRIPTION_LENGTH = 400;
const MAX_DURATION_IN_MINUTES = 60 * 4.5;

const useSessionsScheduleSchema = ({
  now = () => new Date(),
  maxPastMinutesPerDayToStart,
  minPastMinutesPerDayToStart,
  onlyWeekDays,
  notSameDay,
  titleFieldHidden,
  isTitleRequired,
} = {}) => {
  const { t } = useTranslation('components');

  const schema = yup.object().shape({
    description: yup
      .string()
      .trim()
      .max(MAX_DESCRIPTION_LENGTH, t('Sessions.SessionsScheduleForm.Description.maxError')),
    ...(!titleFieldHidden && {
      title: isTitleRequired
        ? yup.string().required(t('global:validation.messages.required'))
        : yup.string(),
    }),
    duration: yup
      .number()
      .truncate()
      .min(0, t('Sessions.SessionsScheduleForm.Duration.minError'))
      .max(MAX_DURATION_IN_MINUTES, t('Sessions.SessionsScheduleForm.Duration.maxError')),
    date: yup
      .string()
      .trim()
      .required(t('global:validation.messages.required'))
      .test('', t('global:validation.messages.wrongDateFormat'), value => {
        if (!value) return undefined;
        return germanDateRegExp.test(value);
      })
      .test('', t('Sessions.SessionsScheduleForm.errors.notSameDay'), value => {
        if (notSameDay) {
          const date = value.split('.').reverse().join('-');
          return !isSameDay(new Date(date), new Date());
        }
        return true;
      })
      .test('', t('Sessions.SessionsScheduleForm.errors.notInThePast'), value => {
        const date = value.split('.').reverse().join('-');
        return date >= format(new Date(), 'YYYY-MM-DD');
      })
      // eslint-disable-next-line func-names
      .test('', t('Sessions.SessionsScheduleForm.errors.timePeriod'), function (value) {
        const { time } = this.parent;
        if (!time || !value || (time && time.length < 5)) return true;
        // Date has to be within 30 days from now
        return isBefore(getFullDate({ date: value, time }), format(addDays(now(), 365)));
      })
      .test('', t('Sessions.SessionsScheduleForm.errors.onlyWeekDays'), value => {
        if (onlyWeekDays) {
          if (isWeekend(getFullDate({ date: value, time: '00:00' }))) return false;
        }
        return true;
      }),
    time: yup
      .string()
      .trim()
      .required(t('global:validation.messages.required'))
      .test('', t('global:validation.messages.wrongTimeFormat'), value => {
        if (!value) return undefined;
        return germanTimeRegExp.test(value);
      })
      .test('', t('Sessions.SessionsScheduleForm.errors.timeRange'), value => {
        if (minPastMinutesPerDayToStart && maxPastMinutesPerDayToStart) {
          return isWithinTimeRange({
            timeString: value,
            minPastMinutesPerDay: minPastMinutesPerDayToStart,
            maxPastMinutesPerDay: maxPastMinutesPerDayToStart,
          });
        }
        return true;
      }),
  });

  return {
    schema,
  };
};

export default useSessionsScheduleSchema;
